import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AccountCard = ({ account, onSync, onDisconnect, onViewTransactions }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'text-success';
      case 'syncing': return 'text-warning';
      case 'error': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected': return 'CheckCircle';
      case 'syncing': return 'RefreshCw';
      case 'error': return 'AlertCircle';
      default: return 'Circle';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    })?.format(amount);
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 border border-border shadow-elevation-2 transition-smooth hover:shadow-elevation-3">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden">
            <Image 
              src={account?.institutionLogo} 
              alt={account?.institutionLogoAlt}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base md:text-lg font-semibold text-foreground truncate">
              {account?.institutionName}
            </h3>
            <p className="text-xs md:text-sm text-muted-foreground truncate">
              {account?.accountType} • {account?.accountNumber}
            </p>
          </div>
        </div>
        <div className={`flex items-center gap-2 ${getStatusColor(account?.status)} flex-shrink-0`}>
          <Icon name={getStatusIcon(account?.status)} size={16} className={account?.status === 'syncing' ? 'animate-spin' : ''} />
          <span className="text-xs md:text-sm font-medium capitalize hidden sm:inline">
            {account?.status}
          </span>
        </div>
      </div>
      <div className="space-y-3 md:space-y-4 mb-4 md:mb-6">
        <div className="flex items-baseline justify-between">
          <span className="text-sm md:text-base text-muted-foreground">Current Balance</span>
          <span className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground">
            {formatCurrency(account?.balance)}
          </span>
        </div>

        {account?.availableBalance && (
          <div className="flex items-center justify-between text-xs md:text-sm">
            <span className="text-muted-foreground">Available Balance</span>
            <span className="font-medium text-foreground">
              {formatCurrency(account?.availableBalance)}
            </span>
          </div>
        )}

        <div className="flex items-center justify-between text-xs md:text-sm">
          <span className="text-muted-foreground">Last Synced</span>
          <span className="font-medium text-foreground">
            {formatDate(account?.lastSynced)}
          </span>
        </div>

        {account?.nextSync && (
          <div className="flex items-center justify-between text-xs md:text-sm">
            <span className="text-muted-foreground">Next Sync</span>
            <span className="font-medium text-foreground">
              {formatDate(account?.nextSync)}
            </span>
          </div>
        )}
      </div>
      <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
        <Button
          variant="default"
          size="sm"
          iconName="Eye"
          iconPosition="left"
          onClick={() => onViewTransactions(account?.id)}
          fullWidth
          className="sm:flex-1"
        >
          View Transactions
        </Button>
        <Button
          variant="outline"
          size="sm"
          iconName="RefreshCw"
          iconPosition="left"
          onClick={() => onSync(account?.id)}
          disabled={account?.status === 'syncing'}
          className="sm:w-auto"
        >
          Sync
        </Button>
        <Button
          variant="ghost"
          size="sm"
          iconName="Unlink"
          onClick={() => onDisconnect(account?.id)}
          className="sm:w-auto text-error hover:text-error"
        >
          Disconnect
        </Button>
      </div>
    </div>
  );
};

export default AccountCard;